<template>
  <div id="filters">
    <div v-for="section in sections" :key="section.title" class="filters-section">
    
      <h5 v-if="section.title !== null">{{ section.title }}</h5>

      <div 
        class="filters-grid"
        :style="{gridTemplateColumns: `repeat(${section.columns}, minmax(0, 1fr))`}"
      >
        <template v-for="block in section.fields" :key="block.key">
          <!-- MULTISELECT -->
          <!-- <FilterControl
            v-if="block.type === 'multiselect'"
            :id="block.key"
            :label="block.label"
            :tooltip-text="tooltipText?.[block.tooltipKey ?? block.key]?.text"
            :options="multiOptions[block.key]"
            :placeholder="block.placeholder ?? 'Not filtering'"
            :model-value="model[block.key]"
            @update:modelValue="(val) => setFilter(block.key, val)"
          /> -->
          <FilterControl
            v-if="block.type === 'multiselect'"
            :id="block.key"
            :label="block.label"
            :tooltip-text="block.tooltipText"
            :options="multiOptions[block.key]"
            :placeholder="block.placeholder ?? 'Not filtering'"
            :model-value="model[block.key]"
            @update:modelValue="(val) => setFilter(block.key, val)"
          />

          <!-- CHECKBOX -->
          <div v-else-if="block.type === 'checkbox'" class="control">
            <div class="flex-row">
              <label class="checkbox-field form-label" :for="block.key">
                <input
                  type="checkbox"
                  :id="block.key"
                  :checked="!!model[block.key]"
                  @change="(e) => setFilter(block.key, e.target.checked)"
                />
                {{ block.label }}
              </label>

              <div
                v-if="block.tooltipText"
                class="tooltip-container"
                @mouseenter="(e) => openTooltip(e, block)"
                @mouseleave="closeTooltip"
                @focus="(e) => openTooltip(e, block)"
                @blur="closeTooltip"
                tabindex="0"
              >
                <i class="icon-help fa fa-circle-question"></i>
              </div>
            </div>
          </div>

          <!-- TOGGLE -->
          <div v-else-if="block.type === 'toggle'" class="control">
            <div class="flex-row">
              <label class="form-label" :for="block.key">{{ block.label }}</label>

              <div
                v-if="block.tooltipText"
                class="tooltip-container"
                @mouseenter="(e) => openTooltip(e, block)"
                @mouseleave="closeTooltip"
                @focus="(e) => openTooltip(e, block)"
                @blur="closeTooltip"
                tabindex="0"
              >
                <i class="icon-help fa fa-circle-question"></i>
              </div>
            </div>

            <div class="toggle-switch">
              <span
                class="toggle-label"
                :class="{ active: !model[block.key] }"
                @click="setFilter(block.key, false)"
              >{{ block.offLabel ?? 'No' }}</span>

              <label class="toggle-track">
                <input
                  type="checkbox"
                  :id="block.key"
                  role="switch"
                  :aria-checked="!!model[block.key]"
                  :checked="!!model[block.key]"
                  @change="(e) => setFilter(block.key, e.target.checked)"
                />
                <span class="toggle-slider" aria-hidden="true"></span>
              </label>

              <span
                class="toggle-label"
                :class="{ active: !!model[block.key] }"
                @click="setFilter(block.key, true)"
              >{{ block.onLabel ?? 'Yes' }}</span>
            </div>
          </div>

          <!-- RADIO -->
          <div v-else-if="block.type === 'radio'" class="control">
            <div class="flex-row">
              <label class="form-label">{{ block.label }}</label>

              <div
                v-if="block.tooltipText"
                class="tooltip-container"
                @mouseenter="(e) => openTooltip(e, block)"
                @mouseleave="closeTooltip"
                @focus="(e) => openTooltip(e, block)"
                @blur="closeTooltip"
                tabindex="0"
              >
                <i class="icon-help fa fa-circle-question"></i>
              </div>
            </div>

            <div class="radio-group" role="radiogroup">
              <label
                v-for="opt in block.radioOptions"
                :key="`${block.key}-${opt.value}`"
                class="radio"
                :for="`${block.key}-${opt.value}`"
              >
                <input
                  type="radio"
                  :id="`${block.key}-${opt.value}`"
                  :name="block.key"
                  :value="opt.value"
                  :checked="model[block.key] === opt.value"
                  @change="() => {
                    if (block.key === 'geographyType') {
                      onGeographyTypeChange(opt.value)
                    } else {
                      setFilter(block.key, opt.value)
                    }
                  }"/>
                {{ opt.label }}
              </label>
            </div>
          </div>
        </template>
      </div>
    </div>

    <button class="btn core-clear-btn mt-1" id="resetFilters" @click="$emit('reset')">
      Reset Filters
    </button>
  </div>
</template>

<script setup>
import { computed, inject } from "vue"
import FilterControl from "./FilterControl.vue"

const tooltipApi = inject("tooltip", null)

const props = defineProps({
  modelValue: { type: Object, required: true }, // your filters object
  options: { type: Object, default: () => ({}) }, // raw arrays keyed by filter
  program: { type: String, default: "core" }, // to conditionally hide filters
})

const emit = defineEmits(["update:modelValue", "reset", "geography-change"])

function onGeographyTypeChange(value) {
  emit('geography-change')  // tell parent immediately
  // then update the model
  emit('update:modelValue', { ...props.modelValue, geographyType: value })
}

const model = computed(() => props.modelValue)
const setFilter = (key, value) => {
  emit("update:modelValue", { 
    ...props.modelValue, 
    [key]: value })
}

const baseSections = [
  {
    title: "Filter by Redemption Status",
    columns: 1,
    fields: [
      { type: "multiselect", key: "redemptionStatus", label: "Redemption Status" },
    ],
  },
  {
    title: "Filter by Geography",
    columns: 1,
    fields: [
      {
        type: "radio",
        key: "geographyType",
        label: "Geography Type",
        // tooltipText: "Choose which geographic boundary to group and filter vouchers by.",
        radioOptions: [
          { value: "county", label: "County" },
          { value: "assemblyDistrict", label: "Assembly District" },
          { value: "senateDistrict", label: "Senate District" },
          { value: "airDistrict", label: "Air Quality District" },
          { value: "censusTract", label: "Census Tract" },
          { value: "zipCode", label: "ZIP Code" },
          { value: "port", label: "Port", hideForPrograms: ["sore"] },
        ],
      },
      { type: "multiselect", key: "region", label: "Region" },
    ],
  },
{
    title: "Other Filters",
    columns: 2,
    fields: [
      { type: "multiselect", key: "equipmentType", label: "Equipment Type" },
      { type: "multiselect", key: "equipmentCategory", label: "Battery/Charger or Tool" },
      { type: "multiselect", key: "voucherRequestYear", label: "Voucher Request Year" },
      { type: "multiselect", key: "vocationalUse", label: "Vocational Use" },
      { type: "multiselect", key: "purchaser", label: "Purchaser Entity" },
      { type: "multiselect", key: "manufacturer", label: "Manufacturer" },
      { type: "multiselect", key: "dealership", label: "Dealership" },
      { type: "multiselect", key: "evse", label: "Infrastructure" },
      { type: "multiselect", key: "smallBusiness", label: "Small Business Fleet" },
      { type: "multiselect", key: "businessClassification", label: "Business Classification" },
      { type: "multiselect", key: "dac", label: "Disadvantaged Community" },
      { 
        type: "multiselect", 
        key: "domicileDac", 
        label: "Disadvantaged Community", 
        tooltipText: "DAC/LIC community designation was verified using the California Communities Environmental Health Screening Tool (CalEnviroScreen) boundaries in effect at the time of voucher request review.",
      },
      { type: "multiselect", key: "portOperations", label: "Port Operations" },
    ],
  },
  {
    title: null,
    columns: 1,
    fields: [
      {
        type: "checkbox",
        key: "includeInfrastructureFunding",
        label: "Include infrastructure voucher funding",
      }
    ]
  },
]

const hiddenFiltersByProgram = {
  sore: ["redemptionStatus", "voucherRequestYear", "vocationalUse", "evse", "smallBusiness", "portOperations", "dealership", "domicileDac", "includeInfrastructureFunding"],
  core: ["equipmentCategory", "businessClassification", "evse", "dac"],
}

const sections = computed(() => {
  const hidden = hiddenFiltersByProgram[props.program] ?? []

  return baseSections
    .map(section => ({
      ...section,
      fields: section.fields
        .filter(field => !hidden.includes(field.key))
        .map(field => {
          if (field.type !== "radio") return field

          return {
            ...field,
            radioOptions: field.radioOptions.filter(
              opt => !(opt.hideForPrograms ?? []).includes(props.program)
            ),
          }
        }),
    }))
    .filter(section => section.fields.length > 0)
})

// Convert raw arrays -> [{label,value}] for each multiselect
const multiOptions = computed(() => {
  const toLV = (arr) => (arr ?? []).map(v => ({ label: String(v), value: v }))
  const out = {}
  for (const section of sections.value) {
    for (const b of section.fields) {
      if (b.type === "multiselect") out[b.key] = toLV(props.options?.[b.key])
    }
  }
  return out
})

// Tooltips for radio (since FilterControl covers multiselect tooltips)
const openTooltip = (event, block) => {
  const text = block.tooltipText
  if (!tooltipApi || !text) return
  tooltipApi.openTooltip(event.currentTarget, text)
}
const closeTooltip = () => tooltipApi?.closeTooltip?.()
</script>
